import Link from 'next/link';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import { isPreviewCreatorId } from '@ps/functions/profile/isPreviewCreatorId';
import { PopImage } from '@ps/ui/atoms/pop-image';
import { ExternalProductLinkIcon } from '@ps/ui/atoms/type-badges/ExternalProductLinkIcon';
import { WearWithAiBadge } from '@ps/ui/atoms/type-badges/WearWithAiBadge';

import { DigitalProductBadge, MeetingSlotBadge } from '../ProductBadges';
import type {
  ProductTileBadgeKind,
  ProductTileViewModel,
} from '../types/ProductTileViewModel';

/**
 * Tile aspect (width / height) — the legacy image (130) + title row (18) folded
 * into one overlaid cell (148 tall), so the tile is all thumbnail and the title
 * sits inside it at the bottom.
 */
const TILE_ASPECT_WIDTH = 103;
const TILE_ASPECT_HEIGHT = 148;
/** Badge glyph size in the top-right corner stack. */
const BADGE_SIZE = 22;
/** Default pixel resolution to request each thumbnail at (crispness only; the
 *  rendered width is responsive). Height follows the tile aspect. */
const DEFAULT_THUMBNAIL_WIDTH = 220;

interface Props {
  product: ProductTileViewModel;
  /** Live bio url code — the PDP href is built here, at render (via `productHref`). */
  bioContext: BioContext;
  /** Bio base font family, applied to the name + price. */
  fontFamily: string;
  /**
   * Pixel resolution to request the thumbnail at. The rendered tile width is
   * responsive (the rack fits 3 full tiles + a partial 4th peek); this only
   * tunes crispness.
   */
  thumbnailWidth?: number;
  /**
   * Dashboard-only in-place open. When supplied (by the `/my-popstore`
   * orchestrator), the tile becomes a `<button>` that calls this instead of a
   * `<Link>` to the PDP route — the isolated bio route tree 404s on the
   * dashboard, so the PDP opens as a state-driven aside there. The visitor omits
   * it and keeps the crawlable soft-nav `<Link>` + `(.)product` intercept.
   */
  onOpen?: () => void;
  /**
   * True when the host overlays an owner action menu in the tile's top-right
   * corner. The type-badge stack then starts BELOW that slot instead of under
   * the menu — QA: the Digital/Wear-with-AI glyphs were hidden behind the Edit
   * kebab. Mirrors legacy `ProductCell`, which drops the badge stack down when
   * the 3-dots menu is shown and lets it take the corner otherwise.
   */
  hasCornerMenu?: boolean;
  /**
   * `true` (default) — the product name overlays the bottom of the thumbnail on
   * a legibility gradient and the cell rounds only its top, so the tile sits
   * flush on the rack card's footer bar.
   *
   * `false` — the name renders BELOW a fully-rounded thumbnail, matching the
   * owner `ProductCell`'s non-overlay mode. Used where the rail has a section
   * header instead of a footer (the collection custom button).
   */
  nameOverlay?: boolean;
}

function badgeGlyph(kind: ProductTileBadgeKind) {
  switch (kind) {
    case 'digital':
      return <DigitalProductBadge size={BADGE_SIZE} />;
    case 'meeting':
      return <MeetingSlotBadge size={BADGE_SIZE} />;
    case 'wear-me':
      return <WearWithAiBadge size={BADGE_SIZE} />;
    case 'external':
      return <ExternalProductLinkIcon size={BADGE_SIZE} />;
  }
}

/**
 * One product tile as it appears in a collection RACK — pure, intent-agnostic,
 * server-renderable. Wraps an all-thumbnail cell: the product name overlays the
 * bottom of the image (on a legibility gradient) and the price chip + corner
 * badges + brand chip sit over it. The cell rounds only its top (`rounded-t-lg`)
 * so it sits FLUSH on the rack's footer bar — 1:1 with the My Life series clip
 * cell.
 *
 * By default the tile is a `next/link` to the PDP at the intercept-prefixed
 * (`'auto'`) URL, so an in-bio tap soft-navigates and the `(.)product` intercept
 * opens the PDP drawer (a plain `<a>` would hard-load the full page). The
 * `/my-popstore` orchestrator supplies `onOpen` instead — the tile becomes a
 * `<button>` that opens the PDP as a state-driven aside (the bio route tree 404s
 * on the dashboard).
 */
export function ProductTileView({
  product,
  bioContext,
  fontFamily,
  thumbnailWidth = DEFAULT_THUMBNAIL_WIDTH,
  onOpen,
  hasCornerMenu = false,
  nameOverlay = true,
}: Props) {
  const thumbnailResizing = {
    width: Math.round(thumbnailWidth),
    height: Math.round(
      (thumbnailWidth * TILE_ASPECT_HEIGHT) / TILE_ASPECT_WIDTH,
    ),
    fit: 'cover' as const,
  };

  const className =
    'focus-visible:ring-brand/40 block w-full rounded-lg text-left focus:outline-none focus-visible:ring-2';
  const style = { fontFamily };
  // The AI-builder preview's pseudo seller has no navigable PDP — its product
  // URL carries the reserved `kPseudoCreatorIdForPreview`, which 500s the PDP
  // route. Render the tile inert there (see the `isPreview` branch below).
  const isPreview = isPreviewCreatorId(bioContext.creatorId);

  const imageCell = (
    <div
      className={`relative w-full overflow-hidden ${
        nameOverlay ? 'rounded-t-lg' : 'rounded-lg'
      }`}
      style={{ aspectRatio: `${TILE_ASPECT_WIDTH} / ${TILE_ASPECT_HEIGHT}` }}
    >
      {product.imageUrl ? (
        <PopImage
          src={product.imageUrl}
          alt={product.name}
          fill
          resizing={thumbnailResizing}
          sizes={`${Math.round(thumbnailWidth)}px`}
        />
      ) : (
        <div className="bg-raise/5 absolute inset-0" />
      )}

      {/* External brand chip — top-left. Suppresses nothing; the external
            link icon still renders in the corner stack (matches legacy). */}
      {product.brandName ? (
        <div
          className="absolute left-0 top-0 z-[7] flex max-w-[60%] items-center"
          style={{
            padding: '4px 6px',
            backgroundColor: 'rgba(0,0,0,0.4)',
            borderTopLeftRadius: 8,
            borderBottomRightRadius: 8,
          }}
        >
          <span
            className="text-content-1 truncate"
            style={{ fontSize: 10, lineHeight: '12px' }}
          >
            {product.brandName}
          </span>
        </div>
      ) : null}

      {/* Corner type-glyph stack — top-right. With an owner menu in the
            corner (`hasCornerMenu`), the stack starts below its slot so the
            glyphs never hide behind the Edit kebab. */}
      {product.badges.length > 0 ? (
        <div
          className={`absolute right-1 z-[8] flex flex-col items-end gap-1 ${
            hasCornerMenu ? 'top-9' : 'top-1'
          }`}
        >
          {product.badges.map(kind => (
            <span key={kind}>{badgeGlyph(kind)}</span>
          ))}
        </div>
      ) : null}

      {/* Price badge — bottom-left overlay above the title. Hidden for FREE
            products, matching legacy `ProductCell` (chip only when `!isFree`). */}
      {product.priceText !== 'FREE' ? (
        <div
          className="absolute left-0 z-[6] flex flex-col items-start"
          style={{ bottom: '28%', opacity: 0.9 }}
        >
          <div
            className="bg-sink/60 flex items-center justify-center"
            style={{
              height: 16,
              paddingLeft: 6,
              paddingRight: 4,
              borderTopRightRadius: 4,
              borderBottomRightRadius: product.originalPriceText ? 0 : 4,
            }}
          >
            <span
              className="text-content-1 font-semibold"
              style={{ fontSize: 12, lineHeight: '11px' }}
            >
              {product.priceText}
            </span>
          </div>
          {product.originalPriceText ? (
            <div
              className="bg-danger flex items-center justify-center"
              style={{
                height: 12,
                paddingLeft: 6,
                paddingRight: 6,
                borderRadius: 8,
                marginLeft: 14,
              }}
            >
              <span
                className="text-content-1 line-through"
                style={{ fontSize: 10, lineHeight: '11px' }}
              >
                {product.originalPriceText}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Bottom gradient + product name overlay — inside the cell, so the tile
            is all thumbnail and sits flush on the rack footer. */}
      {nameOverlay ? (
        <>
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-10"
            style={{
              background:
                'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))',
            }}
          />
          <div className="absolute inset-x-2 bottom-2 z-[6]">
            {/* Name picks up the creator `font_config` via `nameStyle` (1:1
                legacy `Typography.BodyText`); falls back to the baked style
                otherwise. */}
            <span
              className={
                product.nameStyle
                  ? 'block truncate'
                  : 'text-label text-content-1 block truncate font-semibold'
              }
              style={{
                ...product.nameStyle,
                textShadow: '1px 1px 1px rgba(0,0,0,0.5)',
              }}
            >
              {product.name}
            </span>
          </div>
        </>
      ) : null}
    </div>
  );

  const body = (
    <>
      {imageCell}
      {/* Name below the thumbnail — 1:1 with the owner `ProductCell`'s
          non-overlay mode (`mt-2`, semibold, truncated). No text shadow: it
          sits on the bio background, not on an image. */}
      {nameOverlay ? null : (
        <div className="mt-2">
          <span
            className={
              product.nameStyle
                ? 'block truncate font-semibold'
                : 'text-label text-content-1 block truncate font-semibold'
            }
            style={product.nameStyle}
          >
            {product.name}
          </span>
        </div>
      )}
    </>
  );

  // `<button>` when the dashboard opens the PDP in-place (`onOpen`; the bio
  // route tree 404s on `/my-popstore`) OR in the AI-builder preview (pseudo
  // seller, no navigable PDP) — the latter `disabled` so it's honestly inert.
  if (onOpen || isPreview) {
    return (
      <button
        type="button"
        onClick={onOpen}
        disabled={!onOpen}
        aria-label={product.name}
        className={className}
        style={style}
      >
        {body}
      </button>
    );
  }

  // Visitor: soft-nav `<Link>` at the `'auto'` (intercept) prefix so the
  // `(.)product` intercept opens the PDP drawer.
  return (
    <Link
      href={bioUrl(bioContext).product(
        { id: product.id, slug: product.slug },
        'auto',
      )}
      aria-label={product.name}
      className={className}
      style={style}
    >
      {body}
    </Link>
  );
}
